<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useDemoChat, type Message } from '@/composables/useDemoChat';
import DemoChatPanel from './DemoChatPanel.vue';

type LlmStyle = 'chatgpt' | 'claude' | 'claude-code';

const props = withDefaults(defineProps<{
  llmStyle?: LlmStyle;
}>(), {
  llmStyle: 'chatgpt',
});

interface Flashcard {
  question: string;
  answer: string;
}

// Demo script
const conversation: Message[] = [
  { role: 'user', content: 'Can you make me flashcards to teach me about spaced repetition?' },
  { role: 'assistant', content: "I'll create some flashcards covering the key concepts of spaced repetition." },
];

const allFlashcards: Flashcard[] = [
  { question: 'What is the forgetting curve?', answer: 'Memory drops to ~20% within a month without review' },
  { question: 'What is the testing effect?', answer: 'Actively recalling strengthens memory more than re-reading' },
  { question: 'What is the optimal retention rate?', answer: '85-90% - below you forget too much, above you over-review' },
  { question: 'What are leeches?', answer: 'Cards you repeatedly fail, usually need rewriting' },
  { question: 'What is interleaving?', answer: 'Mixing topics during study improves long-term retention' },
];

const {
  visibleMessages, isTyping, typingContent, typingRole, demoVisible,
  sleep, showUserMessage, showAssistantMessage, reset, fadeIn, cleanup,
  MESSAGE_DELAY,
} = useDemoChat();

// Demo-specific state
const visibleCards = ref<Flashcard[]>([]);
const showDeck = ref(false);
const CARD_DELAY = 200;

async function runDemo() {
  reset();
  visibleCards.value = [];
  showDeck.value = false;

  await fadeIn();

  await showUserMessage(conversation[0]);
  await sleep(MESSAGE_DELAY);
  await showAssistantMessage(conversation[1]);

  // Show deck and add cards one by one
  await sleep(300);
  showDeck.value = true;
  await sleep(200);

  for (const card of allFlashcards) {
    visibleCards.value.push(card);
    await sleep(CARD_DELAY);
  }
}

onMounted(() => runDemo());
onUnmounted(() => cleanup());

const cardCount = computed(() => visibleCards.value.length);
</script>

<template>
  <Transition name="demo">
    <div v-show="demoVisible" class="grid md:grid-cols-2 gap-6">
    <!-- Left: Chat -->
    <DemoChatPanel
      :llm-style="props.llmStyle"
      :visible-messages="visibleMessages"
      :is-typing="isTyping"
      :typing-content="typingContent"
      :typing-role="typingRole"
    />

    <!-- Right: App -->
    <div>
      <h2 class="text-xl font-semibold text-foreground mb-3">Your flashcards in LLM-SR</h2>
      <div class="rounded-lg border border-border bg-card overflow-hidden shadow-md">
      <!-- Header bar -->
      <div class="px-3 py-2 border-b border-border bg-muted/50 flex items-center gap-2">
        <div class="flex gap-1.5">
          <div class="w-2.5 h-2.5 rounded-full bg-red-400/70"></div>
          <div class="w-2.5 h-2.5 rounded-full bg-yellow-400/70"></div>
          <div class="w-2.5 h-2.5 rounded-full bg-green-400/70"></div>
        </div>
      </div>

      <!-- App content -->
      <div class="p-4 h-[220px] md:h-[320px] overflow-hidden">
        <Transition name="fade">
          <div v-if="showDeck">
            <div class="flex items-center justify-between mb-3">
              <h3 class="font-medium text-foreground text-sm">Spaced Repetition</h3>
              <span class="text-xs text-muted-foreground">{{ cardCount }} cards</span>
            </div>

            <div class="space-y-2">
              <TransitionGroup name="card">
                <div
                  v-for="card in visibleCards"
                  :key="card.question"
                  class="px-3 py-2 rounded-md bg-muted/50 border border-border"
                >
                  <p class="text-sm text-foreground">{{ card.question }}</p>
                  <p class="text-xs text-muted-foreground mt-1">{{ card.answer }}</p>
                </div>
              </TransitionGroup>
            </div>
          </div>
          <div v-else>
            <div class="flex items-center justify-between mb-3">
              <h3 class="font-medium text-foreground text-sm">Spaced Repetition</h3>
              <span class="text-xs text-muted-foreground">0 cards</span>
            </div>
            <div class="flex items-center justify-center h-[160px] md:h-[260px] text-muted-foreground/50 border border-dashed border-border rounded-md">
              <p class="text-sm">No flashcards yet</p>
            </div>
          </div>
        </Transition>
      </div>
      </div>
    </div>
  </div>
  </Transition>
</template>

<style scoped>
/* Card transitions */
.card-enter-active {
  transition: all 0.3s ease-out;
}
.card-enter-from {
  opacity: 0;
  transform: translateX(-10px);
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Demo fade in/out */
.demo-enter-active,
.demo-leave-active {
  transition: opacity 0.4s ease;
}
.demo-enter-from,
.demo-leave-to {
  opacity: 0;
}
</style>
